import {
  createCodeableConcept,
  getFirstCodeFromCodableConcepts,
  type RelatedPersonRelationship,
} from '@quromedical/fhir-common';

import { getValidReference } from '../mappings';
import {
  mapFhirPersonToContactModel,
  mapFhirPersonToGeneralModel,
  mapPersonGeneralToFhirPerson,
  mapRelatedPersonContactToFhirPartial,
} from '../person';
import type { WithId } from '../types';
import type { PartialRelatedPerson, RelatedPerson } from './schema';

export const mapModelWithIdToFhirRelatedPerson = (
  relatedPerson: WithId<RelatedPerson>,
): WithId<fhir4.RelatedPerson> => {
  const { id } = relatedPerson;

  const result = mapModelToFhirRelatedPerson(relatedPerson);
  return {
    ...result,
    id,
  };
};

export const mapModelToFhirRelatedPerson = (
  relatedPerson: RelatedPerson,
): fhir4.RelatedPerson => {
  const { patient, contact, general, relationship } = relatedPerson;

  const generalResult = mapPersonGeneralToFhirPerson(general);
  const contactResult = mapRelatedPersonContactToFhirPartial(contact);

  return {
    ...generalResult,
    ...contactResult,
    resourceType: 'RelatedPerson',
    active: true,
    patient,
    relationship: [createCodeableConcept(relationship)],
  };
};

export const mapFhirToRelatedPersonModel = (
  fhir: fhir4.RelatedPerson,
): PartialRelatedPerson => {
  const contact = mapFhirPersonToContactModel(fhir);
  const general = mapFhirPersonToGeneralModel(fhir);
  const relationship =
    getFirstCodeFromCodableConcepts<RelatedPersonRelationship>(
      fhir.relationship,
    );

  const patient = getValidReference(fhir.patient);

  return {
    contact,
    general,
    relationship,
    patient,
  };
};

export const mapFhirToRelatedPersonModelWithId = (
  fhir: WithId<fhir4.RelatedPerson>,
): WithId<PartialRelatedPerson> => {
  const result = mapFhirToRelatedPersonModel(fhir);
  return {
    id: fhir.id,
    ...result,
  };
};
